import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Bell } from 'lucide-react';
import { getApiUrl, API_ENDPOINTS } from '../config/api';
import './AgentDashboard.css';
import './Sidebar.css';
import './PaidNoCertAttempt.css';
import './CompExtBell.css';
import KYCSection from './KYCSection';
import SelfRegistrationOnboarding from './SelfRegistrationOnboarding';
import CoverPurchase from './CoverPurchase';
import AgentSidebar from './AgentSidebar';
import AgentBusinessAnalytics from './agent/AgentBusinessAnalytics';
import CompContinuations from './agent/CompContinuations';

const COMP_EXT_TOAST_SESSION_KEY = 'agent_comp_extend_toast_seen_v1';

const AgentDashboard = ({ user, onLogout, onBackToMain }) => {
  const [business, setBusiness] = useState([]);
  const [statistics, setStatistics] = useState({
    totalTransactions: 0,
    totalRevenue: 0,
    completedPayments: 0,
    totalCommission: 0,
    totalTax: 0,
    netCommission: 0
  });
  const [charts, setCharts] = useState({ monthly: [], coverDistribution: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showDeclarations, setShowDeclarations] = useState(false);
  const [showEmployees, setShowEmployees] = useState(false);
  const [declarations, setDeclarations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState('');
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({
    username: '',
    password: '',
    email: '',
    phone: '',
    full_name: ''
  });
  const [declarationsLoading, setDeclarationsLoading] = useState(false);
  const [declarationsError, setDeclarationsError] = useState('');
  const [declarationsPagination, setDeclarationsPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [declarationsSearchTerm, setDeclarationsSearchTerm] = useState('');
  const [declarationsFilterEmployee, setDeclarationsFilterEmployee] = useState('');
  const [showDeclarationsFilters, setShowDeclarationsFilters] = useState(false);
  const [declarationsEmployees, setDeclarationsEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeCovers, setEmployeeCovers] = useState([]);
  const [employeeCoversLoading, setEmployeeCoversLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ 
    username: '',
    email: '', 
    phone: ''
  });
  const [showPaymentMethodForm, setShowPaymentMethodForm] = useState(false);
  const [paymentMethodForm, setPaymentMethodForm] = useState({
    paymentMethod: '',
    mpesaNumber: '',
    bankName: '',
    bankAccountNumber: '',
    bankAccountName: '',
    bankBranch: ''
  });
  const [isSavingPaymentMethod, setIsSavingPaymentMethod] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showCoverPurchase, setShowCoverPurchase] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [purchaseEntryStep, setPurchaseEntryStep] = useState('product-selection');
  const [showCertificateRecovery, setShowCertificateRecovery] = useState(false);
  const [showCompContinuations, setShowCompContinuations] = useState(false);
  const [compExtBadge, setCompExtBadge] = useState(0);
  const [compExtUrgent, setCompExtUrgent] = useState(false);
  const [compExtPreview, setCompExtPreview] = useState([]);
  const [compExtBellOpen, setCompExtBellOpen] = useState(false);
  const [compExtBellPos, setCompExtBellPos] = useState({ top: 0, right: 0 });
  const [compExtToast, setCompExtToast] = useState('');
  const compExtBellRef = useRef(null);
  const compExtDropdownRef = useRef(null);
  const [certRecoveryItems, setCertRecoveryItems] = useState([]);
  const [certRecoveryLoading, setCertRecoveryLoading] = useState(false);
  const [certRecoveryError, setCertRecoveryError] = useState('');
  const [certRecoverySearch, setCertRecoverySearch] = useState('');
  const [certRecoveryStatusScope, setCertRecoveryStatusScope] = useState('pending');
  const [certRecoveryPagination, setCertRecoveryPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [certRetryingClientId, setCertRetryingClientId] = useState(null);
  const [certBatchRetrying, setCertBatchRetrying] = useState(false);
  const [certRetryMessage, setCertRetryMessage] = useState(null);
  const [certDownloadingNumber, setCertDownloadingNumber] = useState(null);
  const [certRetryDateModal, setCertRetryDateModal] = useState(null);
  const [certRetryModalStart, setCertRetryModalStart] = useState('');
  const [certRetryModalSuggested, setCertRetryModalSuggested] = useState(null);
  const [certRetryModalSuggestedDb, setCertRetryModalSuggestedDb] = useState(null);
  const [certRetryModalSuggestedDmvic, setCertRetryModalSuggestedDmvic] = useState(null);
  const [certRetryModalOtherEnd, setCertRetryModalOtherEnd] = useState(null);
  const [certRetryModalLoadingSuggested, setCertRetryModalLoadingSuggested] = useState(false);
  const certPollingIntervalRef = useRef(null);
  const showCertificateRecoveryRef = useRef(false);
  const certRecoveryPageRef = useRef(1);

  const fetchProfile = async () => {
    setProfileLoading(true);
    setProfileError('');
    try {
      const token = localStorage.getItem('agentToken');
      if (!token) {
        setProfileError('Not logged in.');
        setProfileLoading(false);
        return;
      }
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout
      const response = await fetch(getApiUrl('/api/agent/me'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('agentToken');
          localStorage.removeItem('agentUser');
          onLogout();
          setProfileLoading(false);
          return;
        }
        setProfileError(data.error || `Failed to load profile (${response.status})`);
        setProfileLoading(false);
        return;
      }

      if (data.success && data.data) {
        setProfile(data.data);
        setEditForm({
          username: data.data.username || '',
          email: data.data.email || '',
          phone: data.data.phone || ''
        });
        setPaymentMethodForm({
          paymentMethod: data.data.paymentMethod || '',
          mpesaNumber: data.data.mpesaNumber || '',
          bankName: data.data.bankName || '',
          bankAccountNumber: data.data.bankAccountNumber || '',
          bankAccountName: data.data.bankAccountName || '',
          bankBranch: data.data.bankBranch || ''
        });
      } else {
        setProfileError(data.error || 'Profile data could not be loaded.');
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      if (err.name === 'AbortError') {
        setProfileError('Request timed out. Check your connection and try again.');
      } else {
        setProfileError(err.message || 'Failed to load profile. Check your connection.');
      }
    } finally {
      setProfileLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setProfileError('');
    setProfileSuccess('');
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setProfileError('');
    setProfileSuccess('');
    // Reset form to original values
    if (profile) {
      setEditForm({
        username: profile.username || '',
        email: profile.email || '',
        phone: profile.phone || ''
      });
      setPaymentMethodForm({
        paymentMethod: profile.paymentMethod || '',
        mpesaNumber: profile.mpesaNumber || '',
        bankName: profile.bankName || '',
        bankAccountNumber: profile.bankAccountNumber || '',
        bankAccountName: profile.bankAccountName || '',
        bankBranch: profile.bankBranch || ''
      });
    }
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    setProfileError('');
    setProfileSuccess('');

    try {
      const token = localStorage.getItem('agentToken');
      const response = await fetch(getApiUrl('/api/agent/profile'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: editForm.username,
          email: editForm.email,
          phone: editForm.phone
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update profile');
      }

      const data = await response.json();
      if (data.success) {
        setProfile(data.data);
        setIsEditing(false);
        setProfileSuccess('Profile updated successfully!');
        setTimeout(() => setProfileSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setProfileError(err.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePaymentMethod = async () => {
    // Validate payment method is selected
    if (!paymentMethodForm.paymentMethod || paymentMethodForm.paymentMethod === '') {
      setProfileError('Please select a payment method (Mpesa or Bank Transfer) to receive commissions.');
      return;
    }
    
    // Validate Mpesa details if Mpesa is selected
    if (paymentMethodForm.paymentMethod === 'mpesa') {
      if (!paymentMethodForm.mpesaNumber || paymentMethodForm.mpesaNumber.trim() === '') {
        setProfileError('Mpesa number is required when payment method is Mpesa.');
        return;
      }
      // Validate Mpesa number format
      const mpesaRegex = /^(0|254)[0-9]{9}$/;
      if (!mpesaRegex.test(paymentMethodForm.mpesaNumber.trim())) {
        setProfileError('Invalid Mpesa number format. Use format: 0712345678 or 254712345678');
        return;
      }
    }
    
    // Validate Bank details if Bank is selected
    if (paymentMethodForm.paymentMethod === 'bank') {
      if (!paymentMethodForm.bankName || paymentMethodForm.bankName.trim() === '') {
        setProfileError('Bank name is required when payment method is Bank.');
        return;
      }
      if (!paymentMethodForm.bankAccountNumber || paymentMethodForm.bankAccountNumber.trim() === '') {
        setProfileError('Bank account number is required when payment method is Bank.');
        return;
      }
      if (!paymentMethodForm.bankAccountName || paymentMethodForm.bankAccountName.trim() === '') {
        setProfileError('Bank account name is required when payment method is Bank.');
        return;
      }
    }
    
    setIsSavingPaymentMethod(true);
    setProfileError('');
    setProfileSuccess('');

    try {
      const token = localStorage.getItem('agentToken');
      const response = await fetch(getApiUrl('/api/agent/profile'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          paymentMethod: paymentMethodForm.paymentMethod,
          mpesaNumber: paymentMethodForm.paymentMethod === 'mpesa' ? paymentMethodForm.mpesaNumber : null,
          bankName: paymentMethodForm.paymentMethod === 'bank' ? paymentMethodForm.bankName : null,
          bankAccountNumber: paymentMethodForm.paymentMethod === 'bank' ? paymentMethodForm.bankAccountNumber : null,
          bankAccountName: paymentMethodForm.paymentMethod === 'bank' ? paymentMethodForm.bankAccountName : null,
          bankBranch: paymentMethodForm.paymentMethod === 'bank' ? paymentMethodForm.bankBranch : null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update payment method');
      }

      const data = await response.json();
      if (data.success) {
        setProfile(data.data);
        setPaymentMethodForm({
          paymentMethod: data.data.paymentMethod || '',
          mpesaNumber: data.data.mpesaNumber || '',
          bankName: data.data.bankName || '',
          bankAccountNumber: data.data.bankAccountNumber || '',
          bankAccountName: data.data.bankAccountName || '',
          bankBranch: data.data.bankBranch || ''
        });
        setShowPaymentMethodForm(false);
        setProfileSuccess('Payment method saved successfully!');
        setTimeout(() => setProfileSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error updating payment method:', err);
      setProfileError(err.message || 'Failed to update payment method');
    } finally {
      setIsSavingPaymentMethod(false);
    }
  };

  const handleCancelPaymentMethod = () => {
    setShowPaymentMethodForm(false);
    setProfileError('');
    // Reset form to original values
    if (profile) {
      setPaymentMethodForm({
        paymentMethod: profile.paymentMethod || '',
        mpesaNumber: profile.mpesaNumber || '',
        bankName: profile.bankName || '',
        bankAccountNumber: profile.bankAccountNumber || '',
        bankAccountName: profile.bankAccountName || '',
        bankBranch: profile.bankBranch || ''
      });
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setProfileError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setProfileError('Password must be at least 6 characters long');
      return;
    }

    setIsSaving(true);
    setProfileError('');
    setProfileSuccess('');

    try {
      const token = localStorage.getItem('agentToken');
      const response = await fetch(getApiUrl('/api/agent/change-password'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to change password');
      }

      const data = await response.json();
      if (data.success) {
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordChange(false);
        setProfileSuccess('Password changed successfully!');
        setTimeout(() => setProfileSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error changing password:', err);
      setProfileError(err.message || 'Failed to change password');
    } finally {
      setIsSaving(false);
    }
  };

  const fetchBusiness = async (page = 1) => {
    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('agentToken');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate })
      });

      const response = await fetch(getApiUrl('/api/agent/business') + '?' + params, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired
          localStorage.removeItem('agentToken');
          localStorage.removeItem('agentUser');
          onLogout();
          return;
        }
        throw new Error('Failed to fetch business data');
      }

      const data = await response.json();

      if (data.success) {
        setBusiness(data.data.business || []);
        setStatistics(data.data.statistics || statistics);
        setCharts(data.data.charts || { monthly: [], coverDistribution: [] });
        setPagination(data.data.pagination || pagination);
      } else {
        throw new Error(data.error || 'Failed to fetch business data');
      }
    } catch (err) {
      console.error('Error fetching business:', err);
      setError(err.message || 'Failed to load business data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    showCertificateRecoveryRef.current = showCertificateRecovery;
  }, [showCertificateRecovery]);

  const fetchCertificateRecovery = async (page = 1) => {
    setCertRecoveryLoading(true);
    setCertRecoveryError('');
    certRecoveryPageRef.current = page;
    try {
      const token = localStorage.getItem('agentToken');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: certRecoveryPagination.limit.toString(),
        statusScope: certRecoveryStatusScope,
        ...(certRecoverySearch && String(certRecoverySearch).trim() && { search: certRecoverySearch.trim() })
      });
      const response = await fetch(getApiUrl('/api/agent/certificate-recovery') + '?' + params, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('agentToken');
          localStorage.removeItem('agentUser');
          onLogout();
          return;
        }
        throw new Error('Failed to fetch certificate recovery list');
      }
      const data = await response.json();
      if (data.success) {
        setCertRecoveryItems(data.data.items || []);
        setCertRecoveryPagination(data.data.pagination || certRecoveryPagination);
      } else {
        throw new Error(data.error || 'Failed to load list');
      }
    } catch (err) {
      console.error('Certificate recovery fetch:', err);
      setCertRecoveryError(err.message || 'Failed to load certificate recovery list');
    } finally {
      setCertRecoveryLoading(false);
    }
  };

  const stopCertPolling = () => {
    if (certPollingIntervalRef.current) {
      clearInterval(certPollingIntervalRef.current);
      certPollingIntervalRef.current = null;
    }
  };

  const refreshListsAfterCertEvent = () => {
    fetchBusiness(pagination.page);
    if (showCertificateRecoveryRef.current) {
      fetchCertificateRecovery(certRecoveryPageRef.current);
    }
  };

  const retryCertificateForClient = async (clientId, rectification = null, onQueued = null) => {
    setCertRetryMessage(null);
    stopCertPolling();
    setCertRetryingClientId(clientId);
    try {
      const token = localStorage.getItem('agentToken');
      if (!token) {
        setCertRetryMessage('Please log in again.');
        return;
      }
      const response = await fetch(getApiUrl('/api/agent/certificate-retry'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ clientId, ...(rectification || {}) })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setCertRetryMessage(data.error || 'Failed to queue retry.');
        return;
      }
      if (typeof onQueued === 'function') {
        try {
          onQueued();
        } catch (_) { /* ignore */ }
      }
      const jobId = data.jobId;
      if (!jobId && data.success && data.certificateNumber) {
        setCertRetryingClientId(null);
        setCertRetryMessage(
          data.alreadyIssued
            ? (data.message || `Certificate ${data.certificateNumber} is already active.`)
            : `Certificate issued: ${data.certificateNumber}`
        );
        refreshListsAfterCertEvent();
        setTimeout(() => { setCertRetryMessage(null); }, 5000);
        return;
      }
      setCertRetryMessage('Certificate queued for retry. Checking status…');
      refreshListsAfterCertEvent();
      if (!jobId) {
        setCertRetryingClientId(null);
        setTimeout(() => { setCertRetryMessage(null); }, 4000);
        return;
      }
      const pollMs = 1000;
      const maxAttempts = 90;
      let attempts = 0;
      certPollingIntervalRef.current = setInterval(async () => {
        attempts++;
        if (attempts > maxAttempts) {
          stopCertPolling();
          setCertRetryingClientId(null);
          setCertRetryMessage('Status check timed out. Refresh the list to see the latest.');
          refreshListsAfterCertEvent();
          setTimeout(() => setCertRetryMessage(null), 6000);
          return;
        }
        try {
          const statusRes = await fetch(getApiUrl(`/api/certificate-status/${jobId}?clientId=${encodeURIComponent(clientId)}`));
          const statusData = await statusRes.json().catch(() => ({}));
          const state = statusData.state || statusData.status;
          if (statusData.downloadReady || statusData.certificateNumber || state === 'completed' || state === 'issued') {
            stopCertPolling();
            setCertRetryingClientId(null);
            setCertRetryMessage(
              statusData.certificateNumber
                ? `Certificate ${statusData.certificateNumber} is ready.`
                : 'Certificate issued successfully. List updated.'
            );
            setTimeout(() => setCertRetryMessage(null), 5000);
            refreshListsAfterCertEvent();
            return;
          }
          if (state === 'failed' && !statusData.downloadReady && !statusData.certificateNumber) {
            stopCertPolling();
            setCertRetryingClientId(null);
            setCertRetryMessage(statusData.error || 'Retry failed. Try again or contact support.');
            setTimeout(() => setCertRetryMessage(null), 6000);
            refreshListsAfterCertEvent();
            return;
          }
        } catch (_) {
          // keep polling
        }
      }, pollMs);
    } catch (err) {
      setCertRetryMessage(err.message || 'Failed to queue retry.');
      setTimeout(() => { setCertRetryMessage(null); }, 5000);
      setCertRetryingClientId(null);
    }
  };

  const retryPendingCertificatesBatch = async () => {
    if (certBatchRetrying) return;
    setCertRetryMessage(null);
    stopCertPolling();
    const pendingRows = (certRecoveryItems || []).filter((row) => {
      const isIssued = row.certificate_status === 'issued' || !!String(row.dmvic_certificate_number || '').trim();
      return !isIssued && !!row.client_id;
    });
    if (pendingRows.length === 0) {
      setCertRetryMessage('No pending covers available for batch recovery.');
      setTimeout(() => setCertRetryMessage(null), 4000);
      return;
    }
    setCertBatchRetrying(true);
    try {
      const token = localStorage.getItem('agentToken');
      if (!token) {
        setCertRetryMessage('Please log in again.');
        return;
      }

      const concurrency = 3;
      let currentIndex = 0;
      let successCount = 0;
      let failureCount = 0;
      const failures = [];

      const runOne = async (row) => {
        try {
          const response = await fetch(getApiUrl('/api/agent/certificate-retry'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ clientId: row.client_id })
          });
          const data = await response.json().catch(() => ({}));
          if (response.ok && data?.success) {
            successCount += 1;
          } else {
            failureCount += 1;
            failures.push(`${row.client_id}: ${data?.error || 'Failed to queue retry'}`);
          }
        } catch (err) {
          failureCount += 1;
          failures.push(`${row.client_id}: ${err.message || 'Failed to queue retry'}`);
        }
      };

      const worker = async () => {
        while (currentIndex < pendingRows.length) {
          const idx = currentIndex++;
          await runOne(pendingRows[idx]);
        }
      };

      await Promise.all(Array.from({ length: Math.min(concurrency, pendingRows.length) }, () => worker()));

      if (failureCount === 0) {
        setCertRetryMessage(`Successfully queued ${successCount} pending cover(s) for certificate recovery.`);
      } else {
        const sample = failures.slice(0, 2).join(' | ');
        setCertRetryMessage(
          `Queued ${successCount}/${pendingRows.length}. Failed ${failureCount}. ${sample}${failures.length > 2 ? ' …' : ''}`
        );
      }
      refreshListsAfterCertEvent();
      setTimeout(() => setCertRetryMessage(null), 7000);
    } finally {
      setCertBatchRetrying(false);
    }
  };

  const openFixAndRetry = (row) => {
    if (!row?.client_id) return;
    const currentEmail = row.email_address || '';
    const currentPhone = row.phone_number || '';
    const emailInput = window.prompt('Enter corrected email address (leave blank to keep current):', currentEmail);
    if (emailInput === null) return;
    const phoneInput = window.prompt('Enter corrected phone number (07..., 01..., 7..., 1..., or 2547/2541...; leave blank to keep current):', currentPhone);
    if (phoneInput === null) return;
    const payload = {};
    if (String(emailInput).trim()) payload.emailAddress = String(emailInput).trim();
    if (String(phoneInput).trim()) payload.phoneNumber = String(phoneInput).trim();
    retryCertificateForClient(row.client_id, payload);
  };

  const correctPlateNtsaMismatchAndRecover = async (row) => {
    if (!row?.client_id) return;
    const def = (row.registration_number || '').trim();
    const plate = window.prompt('Enter the correct full registration as on NTSA (e.g. KAN269U):', def);
    if (plate == null) return;
    const trimmed = String(plate).trim();
    if (!trimmed) {
      setCertRetryMessage('Enter a registration number.');
      setTimeout(() => setCertRetryMessage(null), 4000);
      return;
    }
    setCertRetryMessage(null);
    setCertRetryingClientId(row.client_id);
    try {
      const token = localStorage.getItem('agentToken');
      if (!token) {
        setCertRetryMessage('Please log in again.');
        setCertRetryingClientId(null);
        return;
      }
      const res = await fetch(getApiUrl('/api/agent/correct-plate-ntsa-mismatch'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clientId: row.client_id, correctRegistrationNumber: trimmed })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setCertRetryMessage(data.error || 'Could not update plate from NTSA.');
        setCertRetryingClientId(null);
        setTimeout(() => setCertRetryMessage(null), 7000);
        refreshListsAfterCertEvent();
        return;
      }
      await retryCertificateForClient(row.client_id);
    } catch (e) {
      setCertRetryMessage(e.message || 'Request failed');
      setCertRetryingClientId(null);
      setTimeout(() => setCertRetryMessage(null), 6000);
      refreshListsAfterCertEvent();
    }
  };

  const closeCertRetryDateModal = () => {
    setCertRetryDateModal(null);
    setCertRetryModalStart('');
    setCertRetryModalSuggested(null);
    setCertRetryModalSuggestedDb(null);
    setCertRetryModalSuggestedDmvic(null);
    setCertRetryModalOtherEnd(null);
    setCertRetryModalLoadingSuggested(false);
  };

  const openCertRetryDateModal = async (row) => {
    if (!row?.client_id) return;
    setCertRetryDateModal(row);
    setCertRetryModalSuggested(null);
    setCertRetryModalSuggestedDb(null);
    setCertRetryModalSuggestedDmvic(null);
    setCertRetryModalOtherEnd(null);
    setCertRetryModalStart('');
    setCertRetryModalLoadingSuggested(true);
    try {
      const token = localStorage.getItem('agentToken');
      const res = await fetch(
        `${getApiUrl('/api/agent/suggested-certificate-start-date')}?clientId=${encodeURIComponent(row.client_id)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        setCertRetryModalSuggestedDb(data.suggestedStartDateFromDb || null);
        setCertRetryModalSuggestedDmvic(data.suggestedStartDateFromDmvic || null);
        setCertRetryModalOtherEnd(data.latestOtherPolicyEndDate || null);
        if (data.suggestedStartDate) {
          setCertRetryModalSuggested(data.suggestedStartDate);
          setCertRetryModalStart(data.suggestedStartDate);
        }
      }
    } catch (_) {
      /* keep today default */
    } finally {
      setCertRetryModalLoadingSuggested(false);
    }
  };

  const submitCertRetryWithDate = () => {
    const reissueDate = String(certRetryModalSuggested || certRetryModalStart || '').trim().slice(0, 10);
    if (!certRetryDateModal?.client_id || !reissueDate) return;
    retryCertificateForClient(
      certRetryDateModal.client_id,
      { startDate: reissueDate },
      closeCertRetryDateModal
    );
  };

  const downloadCertificate = async (certificateNumber) => {
    if (!certificateNumber || certDownloadingNumber) return;
    setCertDownloadingNumber(certificateNumber);
    try {
      const token = localStorage.getItem('agentToken');
      const res = await fetch(API_ENDPOINTS.getCertificateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ certificateNumber: String(certificateNumber).trim() })
      });
      const result = await res.json().catch(() => ({}));
      if (result.success && result.certificateUrl) {
        const link = document.createElement('a');
        link.href = result.certificateUrl;
        link.download = `Certificate_${certificateNumber}.pdf`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setCertRetryMessage(result.error || 'Could not get certificate download link.');
        setTimeout(() => setCertRetryMessage(null), 5000);
      }
    } catch (err) {
      setCertRetryMessage(err.message || 'Download failed.');
      setTimeout(() => setCertRetryMessage(null), 5000);
    } finally {
      setCertDownloadingNumber(null);
    }
  };

  useEffect(() => {
    return () => { stopCertPolling(); };
  }, []);

  const fetchDeclarations = async (page = 1) => {
    setDeclarationsLoading(true);
    setDeclarationsError('');

    try {
      const token = localStorage.getItem('agentToken');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: declarationsPagination.limit.toString()
      });

      // Add search term if provided
      if (declarationsSearchTerm) {
        params.append('search', declarationsSearchTerm);
      }

      // Add employee filter if provided
      if (declarationsFilterEmployee) {
        params.append('employeeId', declarationsFilterEmployee);
      }

      const response = await fetch(getApiUrl('/api/agent/declarations') + '?' + params, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('agentToken');
          localStorage.removeItem('agentUser');
          onLogout();
          return;
        }
        throw new Error('Failed to fetch declarations');
      }

      const data = await response.json();

      if (data.success) {
        setDeclarations(data.data.declarations || []);
        setDeclarationsPagination(data.data.pagination || declarationsPagination);
      } else {
        throw new Error(data.error || 'Failed to fetch declarations');
      }
    } catch (err) {
      console.error('Error fetching declarations:', err);
      setDeclarationsError(err.message || 'Failed to load declarations');
    } finally {
      setDeclarationsLoading(false);
    }
  };

  const exportDeclarations = async () => {
    try {
      const token = localStorage.getItem('agentToken');
      const response = await fetch(getApiUrl('/api/agent/declarations/export'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export declarations');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agent-declarations-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error exporting declarations:', err);
      setDeclarationsError('Failed to export declarations');
    }
  };

  useEffect(() => {
    fetchBusiness(1);
    fetchProfile();
  }, []);

  // Refetch profile when user opens the Profile tab so we always have fresh data and clear stuck loading
  useEffect(() => {
    if (showProfile) {
      fetchProfile();
    }
  }, [showProfile]);

  useEffect(() => {
    if (showDeclarations) {
      fetchDeclarations(1);
      // Also fetch employees for the filter dropdown
      fetchEmployees();
    }
  }, [showDeclarations]);

  // Refetch declarations when search term changes (with debounce)
  useEffect(() => {
    if (!showDeclarations) return;
    
    const timeoutId = setTimeout(() => {
      fetchDeclarations(1);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [declarationsSearchTerm, declarationsFilterEmployee]);

  useEffect(() => {
    if (showEmployees) {
      fetchEmployees();
    }
  }, [showEmployees]);

  const fetchEmployees = async () => {
    setEmployeesLoading(true);
    setEmployeesError('');

    try {
      const token = localStorage.getItem('agentToken');
      const response = await fetch(getApiUrl('/api/agent/users'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('agentToken');
          localStorage.removeItem('agentUser');
          onLogout();
          return;
        }
        throw new Error('Failed to fetch employees');
      }

      const data = await response.json();

      if (data.success) {
        const employeesList = data.users || data.data || [];
        setEmployees(employeesList);
        // Also set for declarations filter
        setDeclarationsEmployees(employeesList);
      } else {
        throw new Error(data.error || 'Failed to fetch employees');
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
      setEmployeesError(err.message || 'Failed to load employees');
    } finally {
      setEmployeesLoading(false);
    }
  };

  const handleCreateEmployee = async () => {
    if (!employeeForm.password) {
      setEmployeesError('Password is required');
      return;
    }

    if (employeeForm.password.length < 6) {
      setEmployeesError('Password must be at least 6 characters long');
      return;
    }

    // Username or full_name is required for auto-generation
    if (!employeeForm.username && !employeeForm.full_name) {
      setEmployeesError('Either username or full name is required');
      return;
    }

    setEmployeesLoading(true);
    setEmployeesError('');

    try {
      const token = localStorage.getItem('agentToken');
      const response = await fetch(getApiUrl('/api/agent/users'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(employeeForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create employee');
      }

      const data = await response.json();

      if (data.success) {
        setEmployeeForm({ username: '', password: '', email: '', phone: '', full_name: '' });
        setShowEmployeeForm(false);
        fetchEmployees();
        if (data.emailSent) {
          setEmployeesError('');
        } else {
          setEmployeesError(data.message || 'Employee created. Email was not sent—check the email address or share credentials manually.');
        }
      } else {
        throw new Error(data.error || 'Failed to create employee');
      }
    } catch (err) {
      console.error('Error creating employee:', err);
      setEmployeesError(err.message || 'Failed to create employee');
    } finally {
      setEmployeesLoading(false);
    }
  };

  const handleUpdateEmployee = async (employeeId) => {
    if (!employeeForm.username) {
      setEmployeesError('Username is required');
      return;
    }

    setEmployeesLoading(true);
    setEmployeesError('');

    try {
      const token = localStorage.getItem('agentToken');
      const updateData = { ...employeeForm };
      // Don't send password if it's empty (for updates)
      if (!updateData.password) {
        delete updateData.password;
      }

      const response = await fetch(getApiUrl(`/api/agent/users/${employeeId}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update employee');
      }

      const data = await response.json();

      if (data.success) {
        setEmployeeForm({ username: '', password: '', email: '', phone: '', full_name: '' });
        setShowEmployeeForm(false);
        setEditingEmployee(null);
        fetchEmployees();
      } else {
        throw new Error(data.error || 'Failed to update employee');
      }
    } catch (err) {
      console.error('Error updating employee:', err);
      setEmployeesError(err.message || 'Failed to update employee');
    } finally {
      setEmployeesLoading(false);
    }
  };

  const handleToggleEmployeeStatus = async (employeeId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this employee?`)) {
      return;
    }

    setEmployeesLoading(true);
    setEmployeesError('');

    try {
      const token = localStorage.getItem('agentToken');
      const response = await fetch(getApiUrl(`/api/agent/users/${employeeId}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${action} employee`);
      }

      const data = await response.json();

      if (data.success) {
        fetchEmployees();
      } else {
        throw new Error(data.error || `Failed to ${action} employee`);
      }
    } catch (err) {
      console.error(`Error ${action}ing employee:`, err);
      setEmployeesError(err.message || `Failed to ${action} employee`);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm('Are you sure you want to delete this employee? This action cannot be undone.')) {
      return;
    }

    setEmployeesLoading(true);
    setEmployeesError('');

    try {
      const token = localStorage.getItem('agentToken');
      const response = await fetch(getApiUrl(`/api/agent/users/${employeeId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete employee');
      }

      const data = await response.json();

      if (data.success) {
        fetchEmployees();
      } else {
        throw new Error(data.error || 'Failed to delete employee');
      }
    } catch (err) {
      console.error('Error deleting employee:', err);
      setEmployeesError(err.message || 'Failed to delete employee');
    } finally {
      setEmployeesLoading(false);
    }
  };

  const handleResendLoginEmail = async (employee) => {
    if (!employee.email || !String(employee.email).trim()) {
      setEmployeesError('Add an email address for this employee before resending login details.');
      return;
    }
    if (
      !window.confirm(
        'This will generate a new password and email it to the employee. Their current password will stop working. Continue?'
      )
    ) {
      return;
    }

    setEmployeesLoading(true);
    setEmployeesError('');

    try {
      const token = localStorage.getItem('agentToken');
      const response = await fetch(getApiUrl(`/api/agent/users/${employee.id}/resend-credentials`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend login email');
      }

      if (data.success && data.emailSent) {
        setEmployeesError('');
        window.alert(data.message || 'Login details were sent by email.');
      } else {
        setEmployeesError(
          data.error ||
            data.emailWarning ||
            'Could not send email. The password was not changed.'
        );
      }
    } catch (err) {
      console.error('Error resending login email:', err);
      setEmployeesError(err.message || 'Failed to resend login email');
    } finally {
      setEmployeesLoading(false);
    }
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee.id);
    setEmployeeForm({
      username: employee.username || '',
      password: '',
      email: employee.email || '',
      phone: employee.phone || '',
      full_name: employee.full_name || ''
    });
    setShowEmployeeForm(true);
  };

  const handleCancelEmployeeForm = () => {
    setShowEmployeeForm(false);
    setEditingEmployee(null);
    setEmployeeForm({ username: '', password: '', email: '', phone: '', full_name: '' });
    setEmployeesError('');
  };

  const handleViewEmployeeCovers = async (employee) => {
    setSelectedEmployee(employee);
    setEmployeeCoversLoading(true);
    setEmployeesError('');

    try {
      const token = localStorage.getItem('agentToken');
      const response = await fetch(getApiUrl(`/api/agent/users/${employee.id}/covers`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch employee covers');
      }

      const data = await response.json();
      if (data.success) {
        setEmployeeCovers(data.covers || []);
      } else {
        throw new Error(data.error || 'Failed to fetch employee covers');
      }
    } catch (err) {
      console.error('Error fetching employee covers:', err);
      setEmployeesError(err.message || 'Failed to load employee covers');
    } finally {
      setEmployeeCoversLoading(false);
    }
  };

  const handleCloseEmployeeCovers = () => {
    setSelectedEmployee(null);
    setEmployeeCovers([]);
  };

  const handleSearch = () => {
    fetchBusiness(1);
  };

  const handleDateFilter = () => {
    fetchBusiness(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPolicyDateOnly = (dateString) => {
    if (!dateString) return '—';
    const raw = String(dateString).trim();
    const parsed = raw.includes('/')
      ? (() => {
          const [day, month, year] = raw.split('/');
          return new Date(Number(year), Number(month) - 1, Number(day));
        })()
      : new Date(/^\d{4}-\d{2}-\d{2}/.test(raw) ? `${raw.slice(0, 10)}T12:00:00` : raw);
    if (isNaN(parsed.getTime())) return raw;
    return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      completed: { class: 'status-completed', text: 'Completed' },
      success: { class: 'status-completed', text: 'Completed' },
      paid: { class: 'status-completed', text: 'Completed' },
      pending: { class: 'status-pending', text: 'Pending' },
      failed: { class: 'status-failed', text: 'Failed' }
    };
    const statusInfo = statusMap[status?.toLowerCase()] || { class: 'status-unknown', text: status || 'Unknown' };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  // COMP extension bell — same UX as underwriting, scoped to this agent's book
  useEffect(() => {
    let cancelled = false;
    const loadCompExtNotifications = async () => {
      try {
        const token = localStorage.getItem('agentToken');
        if (!token) return;

        const summaryRes = await fetch(getApiUrl('/api/agent/comp-continuations/summary'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const summary = await summaryRes.json().catch(() => ({}));
        if (cancelled) return;

        const actionNeeded = summaryRes.ok && summary.success ? Number(summary.actionNeeded) || 0 : 0;
        const badge = summaryRes.ok && summary.success ? Number(summary.badge) || actionNeeded : 0;
        const urgent = actionNeeded > 0;
        setCompExtBadge(badge);
        setCompExtUrgent(urgent);

        const listRes = await fetch(
          `${getApiUrl('/api/agent/comp-continuations')}?scope=action`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const list = await listRes.json().catch(() => ({}));
        if (cancelled) return;

        const items = listRes.ok && list.success && Array.isArray(list.items) ? list.items.slice(0, 5) : [];
        setCompExtPreview(items);

        if (urgent && badge > 0 && !sessionStorage.getItem(COMP_EXT_TOAST_SESSION_KEY)) {
          sessionStorage.setItem(COMP_EXT_TOAST_SESSION_KEY, '1');
          const plural = badge === 1 ? '' : 's';
          setCompExtToast(
            `You have ${badge} Comprehensive extension${plural} due. Open Extend Comprehensive to request via email.`
          );
          setTimeout(() => setCompExtToast(''), 9000);
        }
      } catch (_) {
        /* non-blocking */
      }
    };

    loadCompExtNotifications();
    const interval = setInterval(loadCompExtNotifications, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const updateCompExtBellPos = useCallback(() => {
    const el = compExtBellRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCompExtBellPos({
      top: Math.round(rect.bottom + 10),
      right: Math.round(Math.max(8, window.innerWidth - rect.right))
    });
  }, []);

  useLayoutEffect(() => {
    if (!compExtBellOpen) return undefined;
    updateCompExtBellPos();
    const onReposition = () => updateCompExtBellPos();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [compExtBellOpen, updateCompExtBellPos]);

  useEffect(() => {
    if (!compExtBellOpen) return undefined;
    const onDocClick = (e) => {
      const inBtn = compExtBellRef.current && compExtBellRef.current.contains(e.target);
      const inMenu = compExtDropdownRef.current && compExtDropdownRef.current.contains(e.target);
      if (!inBtn && !inMenu) setCompExtBellOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setCompExtBellOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [compExtBellOpen]);

  // Handle view changes from sidebar
  const handleViewChange = (view) => {
    setActiveView(view);
    if (view === 'dashboard') {
      setShowProfile(false);
      setShowEmployees(false);
      setShowCoverPurchase(false);
      setShowDeclarations(false);
      setShowCertificateRecovery(false);
      setShowCompContinuations(false);
    } else if (view === 'profile') {
      setShowProfile(true);
      setShowEmployees(false);
      setShowCoverPurchase(false);
      setShowDeclarations(false);
      setShowCertificateRecovery(false);
      setShowCompContinuations(false);
    } else if (view === 'employees') {
      setShowProfile(false);
      setShowEmployees(true);
      setShowCoverPurchase(false);
      setShowDeclarations(false);
      setShowCertificateRecovery(false);
      setShowCompContinuations(false);
    } else if (view === 'purchase-cover') {
      setShowProfile(false);
      setShowEmployees(false);
      setShowCoverPurchase(true);
      setShowDeclarations(false);
      setShowCertificateRecovery(false);
      setShowCompContinuations(false);
      setPurchaseEntryStep('product-selection');
    } else if (view === 'extend-policy') {
      setShowProfile(false);
      setShowEmployees(false);
      setShowCoverPurchase(true);
      setShowDeclarations(false);
      setShowCertificateRecovery(false);
      setShowCompContinuations(false);
      setPurchaseEntryStep('extension-finder');
    } else if (view === 'certificate-recovery') {
      setShowProfile(false);
      setShowEmployees(false);
      setShowCoverPurchase(false);
      setShowDeclarations(false);
      setShowCertificateRecovery(true);
      setShowCompContinuations(false);
    } else if (view === 'comp-continuations') {
      setShowProfile(false);
      setShowEmployees(false);
      setShowCoverPurchase(false);
      setShowDeclarations(false);
      setShowCertificateRecovery(false);
      setShowCompContinuations(true);
    }
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.sidebar') && !event.target.closest('.mobile-menu-btn')) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Update active view based on current state
  useEffect(() => {
    if (showProfile) setActiveView('profile');
    else if (showEmployees) setActiveView('employees');
    else if (showCertificateRecovery) setActiveView('certificate-recovery');
    else if (showCoverPurchase) setActiveView(purchaseEntryStep === 'extension-finder' ? 'extend-policy' : 'purchase-cover');
    else setActiveView('dashboard');
  }, [showProfile, showEmployees, showCoverPurchase, showCertificateRecovery, purchaseEntryStep]);

  useEffect(() => {
    if (showCertificateRecovery) {
      fetchCertificateRecovery(1);
    }
  }, [showCertificateRecovery]);

  useEffect(() => {
    if (showCertificateRecovery) {
      fetchCertificateRecovery(1);
    }
  }, [certRecoveryStatusScope]);

  return (
    <div className="agent-dashboard">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="Close menu"
        />
      )}

      {/* Sidebar Navigation */}
      <AgentSidebar
        activeView={activeView}
        onViewChange={handleViewChange}
        user={user}
        showKycOnboarding={!!profile?.selfRegistered}
        onLogout={async () => {
          const confirmed = window.confirm('Are you sure you want to logout?');
          if (!confirmed) return;
          
          if (onLogout) {
            // Clear local state
            setBusiness([]);
            setStatistics({
              totalTransactions: 0,
              totalRevenue: 0,
              completedPayments: 0
            });
            setProfile(null);
            setShowProfile(false);
            setShowDeclarations(false);
            setIsEditing(false);
            setEditForm({ username: '', email: '', phone: '' });
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setProfileError('');
            setProfileSuccess('');
            setDeclarations([]);
            setDeclarationsError('');
            
            // Perform logout
            await onLogout();
          }
        }}
        onBackToMain={onBackToMain}
        isMobileOpen={isMobileMenuOpen}
        onMobileClose={() => setIsMobileMenuOpen(false)}
      />

      <div className="agent-dashboard-main">
        <header className="dashboard-header">
          <div className="header-content">
            <div className="header-left">
              {/* Mobile Menu Button */}
              <button 
                className="mobile-menu-btn"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
              >
                <span className="menu-icon">☰</span>
              </button>
              
              <div className="header-title-section">
                {/* Agency Name - Always Visible */}
                <div className="agency-name-display">
                  <span className="agency-icon">🏢</span>
                  <span className="agency-text">{user?.agencyName || 'Insurance Agency'}</span>
                </div>
                
                <h1 className="dashboard-title">Agent Dashboard</h1>
                <p className="dashboard-subtitle">
                  {showProfile && 'Manage your profile and settings'}
                  {showEmployees && 'Manage your employees'}
                  {showCoverPurchase && 'Purchase insurance cover'}
                  {showCertificateRecovery && 'Search your covers by vehicle registration, policy number, client ID, insured name, or payment reference to check and fix certificate status'}
                  {showCompContinuations && 'COMP policies due for extension — email requests@ from your mailbox'}
                  {!showProfile && !showEmployees && !showCoverPurchase && !showCertificateRecovery && !showCompContinuations && 'Track your transactions, revenue, and commission earnings'}
                </p>
                <p className="ira-number" style={{ display: 'none' }}>IRA: {user?.iraNumber || 'N/A'}</p>
              </div>
            </div>

            <div className="header-right">
              <nav className="header-nav-bar" aria-label="Agent notifications">
                <div className="comp-ext-bell-wrap" ref={compExtBellRef}>
                  <button
                    type="button"
                    className={`nav-bar-btn comp-ext-bell-btn${compExtUrgent && compExtBadge > 0 ? ' has-alerts' : ''}${compExtBellOpen ? ' is-open' : ''}`}
                    title="Comprehensive extensions"
                    aria-label="Comprehensive extension notifications"
                    aria-expanded={compExtBellOpen}
                    onClick={() => {
                      setCompExtBellOpen((o) => {
                        const next = !o;
                        if (next) requestAnimationFrame(() => updateCompExtBellPos());
                        return next;
                      });
                    }}
                  >
                    <span className="btn-icon comp-ext-bell-icon">
                      <Bell size={18} strokeWidth={2.25} />
                      {compExtBadge > 0 && (
                        <span className="comp-ext-bell-badge">
                          {compExtBadge > 99 ? '99+' : compExtBadge}
                        </span>
                      )}
                    </span>
                    <span className="btn-text">Comprehensive Extensions</span>
                  </button>
                  {compExtBellOpen &&
                    createPortal(
                      <div
                        ref={compExtDropdownRef}
                        className="comp-ext-bell-dropdown"
                        role="menu"
                        style={{ top: compExtBellPos.top, right: compExtBellPos.right }}
                      >
                        <div className="comp-ext-bell-head">
                          <div className="comp-ext-bell-head-row">
                            <span className="comp-ext-bell-head-title">Extend Comprehensive</span>
                            {compExtBadge > 0 && (
                              <span className="comp-ext-bell-count-chip">{compExtBadge} due</span>
                            )}
                          </div>
                          <p className="comp-ext-bell-head-sub">
                            Your COMP policies nearing end of period — request via requests@
                          </p>
                        </div>
                        {compExtPreview.length === 0 ? (
                          <div className="comp-ext-bell-empty">
                            <span className="comp-ext-bell-empty-icon" aria-hidden>◎</span>
                            <p>No Comprehensive extensions need action right now.</p>
                          </div>
                        ) : (
                          <ul className="comp-ext-bell-list">
                            {compExtPreview.map((item) => {
                              const days = item.daysLeft;
                              const daysLabel =
                                days == null
                                  ? '—'
                                  : days < 0
                                    ? `Expired ${Math.abs(days)}d`
                                    : days === 0
                                      ? 'Today'
                                      : `${days}d left`;
                              const urgencyKey = item.urgency?.key || 'ok';
                              return (
                                <li key={item.clientId}>
                                  <button
                                    type="button"
                                    className="comp-ext-bell-item"
                                    onClick={() => {
                                      setCompExtBellOpen(false);
                                      handleViewChange('comp-continuations');
                                    }}
                                  >
                                    <div className="comp-ext-bell-item-top">
                                      <span className="comp-ext-bell-reg">
                                        {item.registrationNumber || 'No reg'}
                                      </span>
                                      <span className={`comp-ext-bell-days urgency-${urgencyKey}`}>
                                        {daysLabel}
                                      </span>
                                    </div>
                                    <span className="comp-ext-bell-insured">
                                      {item.insuredName || 'Insured —'}
                                    </span>
                                    <span className="comp-ext-bell-meta">
                                      {item.urgency?.label || 'Due'}
                                      {item.policyNumber ? ` · ${item.policyNumber}` : ''}
                                    </span>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                        <div className="comp-ext-bell-footer-wrap">
                          <button
                            type="button"
                            className="comp-ext-bell-footer"
                            onClick={() => {
                              setCompExtBellOpen(false);
                              handleViewChange('comp-continuations');
                            }}
                          >
                            Open Extend Comprehensive
                          </button>
                        </div>
                      </div>,
                      document.body
                    )}
                </div>
              </nav>
            </div>
          </div>
        </header>

        {compExtToast && (
          <div className="agent-comp-ext-toast" role="status">
            {compExtToast}
          </div>
        )}

      <main className="agent-dashboard-content">
        {/* Cover Purchase Section */}
        {showCoverPurchase && (
          <CoverPurchase
            agentId={user?.id || user?.username}
            agency={
              (profile?.tqCode != null || profile?.kraPin || user?.tqCode != null || user?.kraPin)
                ? { shortDescription: (profile?.tqCode ?? user?.tqCode) || '', taxPin: (profile?.kraPin ?? user?.kraPin) || '' }
                : undefined
            }
            initialEntryStep={purchaseEntryStep}
            onClose={() => {
              setShowCoverPurchase(false);
              setPurchaseEntryStep('product-selection');
              // Refresh business data after purchase
              fetchBusiness(pagination.page);
            }}
            onPurchaseComplete={(result) => {
              // Refresh statistics and business data
              fetchBusiness(pagination.page);
              // Optionally show success message
            }}
          />
        )}

        {/* Profile Section */}
        {showProfile && !showCoverPurchase && (
          <div className="profile-section" style={{ 
            background: 'white', 
            padding: '2rem', 
            borderRadius: '12px', 
            marginBottom: '2rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setShowProfile(false)}
                  style={{
                    background: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}
                >
                  ← Back to Dashboard
                </button>
                <h2 style={{ margin: 0, color: '#2c3e50' }}>👤 Agent Profile</h2>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                {!isEditing && profile && (
                  <button 
                    onClick={handleEdit}
                    style={{
                      background: '#3498db',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem'
                    }}
                  >
                    ✏️ Edit Profile
                  </button>
                )}
                <button 
                  className="btn-logout"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const confirmed = window.confirm('Are you sure you want to logout?');
                    if (!confirmed) return;
                    if (onLogout) {
                      await onLogout();
                    }
                  }}
                  style={{
                    background: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}
                >
                  🚪 Logout
                </button>
              </div>
            </div>

            {profileError && (
              <div style={{ 
                background: '#fee', 
                color: '#c33', 
                padding: '1rem', 
                borderRadius: '8px', 
                marginBottom: '1rem',
                border: '1px solid #fcc'
              }}>
                ⚠️ {profileError}
              </div>
            )}

            {profileSuccess && (
              <div style={{ 
                background: '#efe', 
                color: '#3c3', 
                padding: '1rem', 
                borderRadius: '8px', 
                marginBottom: '1rem',
                border: '1px solid #cfc'
              }}>
                ✅ {profileSuccess}
              </div>
            )}

            {profile ? (
              <div>
                <div className="profile-grid" style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                  gap: '1.5rem' 
                }}>
                  <div className="profile-item">
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>Username</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.username}
                        onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          marginTop: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                        placeholder="5-15 characters, no spaces"
                      />
                    ) : (
                      <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>{profile.username}</div>
                    )}
                  </div>
                  <div className="profile-item">
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>IRA Number</label>
                    <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>{profile.iraNumber}</div>
                  </div>
                  <div className="profile-item">
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>Agency Name</label>
                    <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>{profile.agencyName}</div>
                  </div>
                  <div className="profile-item">
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>Email *</label>
                    {isEditing ? (
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          marginTop: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                        placeholder="Enter email address"
                      />
                    ) : (
                      <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>{profile.email || 'N/A'}</div>
                    )}
                  </div>
                  <div className="profile-item">
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>Phone</label>
                    {isEditing ? (
                      <input
                        type="tel"
                        value={editForm.phone}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          marginTop: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                        placeholder="Enter phone number"
                      />
                    ) : (
                      <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>{profile.phone || 'N/A'}</div>
                    )}
                  </div>
                  {/* TQ Short Description only (no separate Short Description) */}
                  <div className="profile-item">
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>TQ Short Description</label>
                    <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>{profile.tqCode || profile.shortDescription || 'N/A'}</div>
                  </div>
                  <div className="profile-item">
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>TQ Status</label>
                    <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>{profile.tqStatus || 'N/A'}</div>
                  </div>
                  <div className="profile-item">
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>TQ IRA No</label>
                    <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>{profile.tqIraNo || 'N/A'}</div>
                  </div>
                  <div className="profile-item">
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>TQ Email</label>
                    <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>{profile.tqEmail || 'N/A'}</div>
                  </div>
                  <div className="profile-item">
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>TQ Phone</label>
                    <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>{profile.tqPhone || 'N/A'}</div>
                  </div>
                  <div className="profile-item">
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>TQ ID No</label>
                    <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>{profile.tqIdNo || 'N/A'}</div>
                  </div>
                  <div className="profile-item">
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>Date Licensed</label>
                    <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>{profile.dateLicensed || 'N/A'}</div>
                  </div>
                  <div className="profile-item">
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>Entity Type</label>
                    <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>{profile.entityType || 'N/A'}</div>
                  </div>
                  {/* KYC Information */}
                  <>
                    <div className="profile-item">
                      <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>ID Number</label>
                      <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>{profile.idNumber || 'N/A'}</div>
                    </div>
                    <div className="profile-item">
                      <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>Full Names (KRA)</label>
                      <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>{profile.fullNames || 'N/A'}</div>
                    </div>
                    <div className="profile-item">
                      <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>KRA PIN</label>
                      <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>{profile.kraPin || 'N/A'}</div>
                    </div>
                    <div className="profile-item">
                      <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem' }}>KYC Status</label>
                      <div style={{ fontSize: '1.1rem', color: '#2c3e50', marginTop: '0.5rem' }}>
                        {profile.kycStatus === 'approved' && <span style={{ color: '#27ae60', fontWeight: 'bold' }}>✅ Approved</span>}
                        {(profile.kycStatus === 'pending' || profile.kycStatus === 'pending_review') && <span style={{ color: '#f39c12', fontWeight: 'bold' }}>⏳ Pending Review</span>}
                        {profile.kycStatus === 'rejected' && <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>❌ Rejected</span>}
                        {profile.kycStatus === 'flagged' && <span style={{ color: '#e67e22', fontWeight: 'bold' }}>⚠️ Flagged</span>}
                        {!profile.kycStatus && <span style={{ color: '#95a5a6' }}>Not Started</span>}
                      </div>
                    </div>
                  </>
                  
                  {/* Bank Details Section */}
                  <div className="profile-item" style={{ gridColumn: '1 / -1', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e0e0e0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem', display: 'block' }}>Payment Method for Commission</label>
                      {!showPaymentMethodForm && (
                        <button
                          onClick={() => setShowPaymentMethodForm(true)}
                          style={{
                            background: profile.paymentMethod ? '#3498db' : '#27ae60',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem 1rem',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '600'
                          }}
                        >
                          {profile.paymentMethod ? '✏️ Edit Payment Method' : '➕ Add Payment Method'}
                        </button>
                      )}
                    </div>
                    
                    {showPaymentMethodForm ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="radio"
                              name="paymentMethod"
                              value="mpesa"
                              checked={paymentMethodForm.paymentMethod === 'mpesa'}
                              onChange={(e) => {
                                setPaymentMethodForm({
                                  ...paymentMethodForm,
                                  paymentMethod: e.target.value,
                                  bankName: '',
                                  bankAccountNumber: '',
                                  bankAccountName: '',
                                  bankBranch: ''
                                });
                              }}
                              required
                            />
                            <span>Mpesa</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="radio"
                              name="paymentMethod"
                              value="bank"
                              checked={paymentMethodForm.paymentMethod === 'bank'}
                              onChange={(e) => {
                                setPaymentMethodForm({
                                  ...paymentMethodForm,
                                  paymentMethod: e.target.value,
                                  mpesaNumber: ''
                                });
                              }}
                              required
                            />
                            <span>Bank Transfer</span>
                          </label>
                        </div>
                        {!paymentMethodForm.paymentMethod && (
                          <div style={{ color: '#e74c3c', fontSize: '0.9rem', fontStyle: 'italic' }}>
                            ⚠️ Please select a payment method (Mpesa or Bank Transfer) to receive commissions.
                          </div>
                        )}
                        
                        {paymentMethodForm.paymentMethod === 'mpesa' && (
                          <div>
                            <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                              Mpesa Number *
                            </label>
                            <input
                              type="tel"
                              value={paymentMethodForm.mpesaNumber}
                              onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, mpesaNumber: e.target.value })}
                              placeholder="0712345678 or 254712345678"
                              style={{
                                width: '100%',
                                maxWidth: '400px',
                                padding: '0.75rem',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                fontSize: '1rem'
                              }}
                            />
                            <small style={{ display: 'block', marginTop: '0.25rem', color: '#7f8c8d' }}>
                              Format: 0712345678 or 254712345678
                            </small>
                          </div>
                        )}
                        
                        {paymentMethodForm.paymentMethod === 'bank' && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                            <div>
                              <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                                Bank Name *
                              </label>
                              <input
                                type="text"
                                value={paymentMethodForm.bankName}
                                onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, bankName: e.target.value })}
                                placeholder="e.g., Equity Bank"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '1px solid #ddd',
                                  borderRadius: '6px',
                                  fontSize: '1rem'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                                Account Number *
                              </label>
                              <input
                                type="text"
                                value={paymentMethodForm.bankAccountNumber}
                                onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, bankAccountNumber: e.target.value })}
                                placeholder="Account number"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '1px solid #ddd',
                                  borderRadius: '6px',
                                  fontSize: '1rem'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                                Account Name *
                              </label>
                              <input
                                type="text"
                                value={paymentMethodForm.bankAccountName}
                                onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, bankAccountName: e.target.value })}
                                placeholder="Account holder name"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '1px solid #ddd',
                                  borderRadius: '6px',
                                  fontSize: '1rem'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                                Branch (Optional)
                              </label>
                              <input
                                type="text"
                                value={paymentMethodForm.bankBranch}
                                onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, bankBranch: e.target.value })}
                                placeholder="Branch name"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '1px solid #ddd',
                                  borderRadius: '6px',
                                  fontSize: '1rem'
                                }}
                              />
                            </div>
                          </div>
                        )}
                        
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                          <button
                            onClick={handleSavePaymentMethod}
                            disabled={isSavingPaymentMethod || !paymentMethodForm.paymentMethod}
                            style={{
                              background: (isSavingPaymentMethod || !paymentMethodForm.paymentMethod) ? '#95a5a6' : '#27ae60',
                              color: 'white',
                              border: 'none',
                              padding: '0.75rem 1.5rem',
                              borderRadius: '6px',
                              cursor: (isSavingPaymentMethod || !paymentMethodForm.paymentMethod) ? 'not-allowed' : 'pointer',
                              fontWeight: '600',
                              fontSize: '1rem',
                              minWidth: '150px'
                            }}
                            title={!paymentMethodForm.paymentMethod ? 'Please select a payment method to save' : 'Save payment method'}
                          >
                            {isSavingPaymentMethod ? '💾 Saving...' : '💾 Save Payment Method'}
                          </button>
                          <button
                            onClick={handleCancelPaymentMethod}
                            disabled={isSavingPaymentMethod}
                            style={{
                              background: '#95a5a6',
                              color: 'white',
                              border: 'none',
                              padding: '0.75rem 1.5rem',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '1rem'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ marginTop: '0.5rem' }}>
                        {profile.paymentMethod === 'mpesa' && (
                          <div style={{ fontSize: '1.1rem', color: '#2c3e50', padding: '0.75rem', background: '#f8f9fa', borderRadius: '6px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                              <span style={{ fontWeight: 'bold' }}>Payment Method:</span>
                              <span style={{ color: '#27ae60', fontWeight: '600' }}>Mpesa</span>
                            </div>
                            <div><span style={{ fontWeight: 'bold' }}>Mpesa Number:</span> {profile.mpesaNumber || 'N/A'}</div>
                          </div>
                        )}
                        {profile.paymentMethod === 'bank' && (
                          <div style={{ fontSize: '1.1rem', color: '#2c3e50', padding: '0.75rem', background: '#f8f9fa', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                              <span style={{ fontWeight: 'bold' }}>Payment Method:</span>
                              <span style={{ color: '#27ae60', fontWeight: '600' }}>Bank Transfer</span>
                            </div>
                            <div><span style={{ fontWeight: 'bold' }}>Bank:</span> {profile.bankName || 'N/A'}</div>
                            <div><span style={{ fontWeight: 'bold' }}>Account Number:</span> {profile.bankAccountNumber || 'N/A'}</div>
                            <div><span style={{ fontWeight: 'bold' }}>Account Name:</span> {profile.bankAccountName || 'N/A'}</div>
                            {profile.bankBranch && (
                              <div><span style={{ fontWeight: 'bold' }}>Branch:</span> {profile.bankBranch}</div>
                            )}
                          </div>
                        )}
                        {!profile.paymentMethod && (
                          <div style={{ fontSize: '1.1rem', color: '#95a5a6', fontStyle: 'italic', padding: '0.75rem', background: '#f8f9fa', borderRadius: '6px' }}>
                            No payment method configured. Click "Add Payment Method" to set up your payment details for commission payments.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={handleSaveProfile}
                      disabled={isSaving}
                      style={{
                        background: isSaving ? '#95a5a6' : '#27ae60',
                        color: 'white',
                        border: 'none',
                        padding: '0.75rem 2rem',
                        borderRadius: '8px',
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '1rem',
                        minWidth: '150px'
                      }}
                      title="Save profile changes"
                    >
                      {isSaving ? '💾 Saving...' : '💾 Save Changes'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                      style={{
                        background: '#95a5a6',
                        color: 'white',
                        border: 'none',
                        padding: '0.75rem 2rem',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '1rem'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {/* KYC Section */}
                <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '2px solid #e0e0e0' }}>
                  {profile?.selfRegistered && (
                    <SelfRegistrationOnboarding profile={profile} compact />
                  )}
                  <KYCSection agentId={user?.id} onUpdate={fetchProfile} />
                </div>

                {/* Password Change Section */}
                <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e0e0e0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3 style={{ margin: 0, color: '#2c3e50' }}>Change Password</h3>
                    <button
                      onClick={() => setShowPasswordChange(!showPasswordChange)}
                      style={{
                        background: showPasswordChange ? '#95a5a6' : '#3498db',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      {showPasswordChange ? 'Cancel' : 'Change Password'}
                    </button>
                  </div>

                  {showPasswordChange && (
                    <div style={{ display: 'grid', gap: '1rem' }}>
                      <div>
                        <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                          Current Password
                        </label>
                        <input
                          type="password"
                          value={passwordForm.currentPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '1rem'
                          }}
                          placeholder="Enter current password"
                        />
                      </div>
                      <div>
                        <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                          New Password
                        </label>
                        <input
                          type="password"
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '1rem'
                          }}
                          placeholder="Enter new password (min 6 characters)"
                        />
                      </div>
                      <div>
                        <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '1rem'
                          }}
                          placeholder="Confirm new password"
                        />
                      </div>
                      <button
                        onClick={handleChangePassword}
                        disabled={isSaving || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                        style={{
                          background: (isSaving || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) ? '#95a5a6' : '#27ae60',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem 2rem',
                          borderRadius: '8px',
                          cursor: (isSaving || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          fontSize: '1rem',
                          justifySelf: 'start'
                        }}
                      >
                        {isSaving ? 'Changing...' : '💾 Change Password'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : !profile && profileLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div className="spinner-large"></div>
                <p>Loading profile...</p>
              </div>
            ) : !profile && profileError ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p style={{ color: '#c33', marginBottom: '1rem' }}>⚠️ {profileError}</p>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => fetchProfile()}
                  style={{ padding: '0.75rem 1.5rem' }}
                >
                  Retry
                </button>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                <p>No profile data. <button type="button" className="btn-secondary" onClick={() => fetchProfile()} style={{ marginLeft: '0.5rem' }}>Retry</button></p>
              </div>
            )}
          </div>
        )}

        {/* Declarations Section - Hidden as per requirement (unnecessary for agents/employees) */}
        {false && showDeclarations && !showCoverPurchase && (
          <div className="agent-declarations-container" style={{ 
            background: 'white', 
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            width: '100%'
          }}>
            {/* Header */}
            <div className="declarations-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '2rem',
              borderBottom: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <div className="header-left">
                <h2 style={{ 
                  fontSize: '1.75rem',
                  fontWeight: '700',
                  color: '#1e293b',
                  margin: '0 0 0.5rem 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  📊 Agent Covers & Declarations
                </h2>
                <p style={{ 
                  fontSize: '1rem',
                  color: '#64748b',
                  margin: 0
                }}>
                  View all agent covers and declarations ({declarationsPagination.total || 0} total)
                </p>
              </div>
              <div className="header-actions" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
              }}>
                <input
                  type="text"
                  placeholder="Search declarations..."
                  value={declarationsSearchTerm || ''}
                  onChange={(e) => setDeclarationsSearchTerm(e.target.value)}
                  style={{
                    padding: '0.75rem 1rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    fontSize: '0.9rem',
                    minWidth: '250px'
                  }}
                />
                <button
                  onClick={() => setShowDeclarationsFilters(!showDeclarationsFilters)}
                  style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    color: '#64748b',
                    border: '1px solid #e2e8f0',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '10px',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  🔍 Filters
                </button>
              </div>
            </div>

            {/* Filter Section */}
            <div className="agent-filter-section" style={{
              padding: '1.5rem 2rem',
              borderBottom: '1px solid #e2e8f0',
              background: '#f8fafc'
            }}>
              <div className="agent-filter-header" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: '600',
                  color: '#1e293b',
                  fontSize: '0.9rem'
                }}>
                  <span>👥</span>
                  Filter by Employee:
                </label>
                <select
                  value={declarationsFilterEmployee || ''}
                  onChange={(e) => {
                    setDeclarationsFilterEmployee(e.target.value);
                    fetchDeclarations(1);
                  }}
                  style={{
                    padding: '0.75rem 1rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    minWidth: '300px',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Employees ({declarationsPagination.total || 0} covers)</option>
                  <option value="agent">Agent Direct ({declarationsPagination.total || 0} covers)</option>
                  {declarationsEmployees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.full_name || emp.username} ({declarationsPagination.total || 0} covers)
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {declarationsError && (
              <div style={{ 
                background: '#fee', 
                color: '#c33', 
                padding: '1rem 2rem', 
                borderRadius: '8px', 
                margin: '1rem 2rem',
                border: '1px solid #fcc'
              }}>
                ⚠️ {declarationsError}
              </div>
            )}

            {declarationsLoading ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <div className="spinner-large"></div>
                <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading declarations...</p>
              </div>
            ) : declarations.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '3rem',
                color: '#64748b'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                <h3 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>No declarations found</h3>
                <p>Declarations will appear here after certificates are issued.</p>
              </div>
            ) : (
              <>
                <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                  <table className="declarations-table" style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.9rem'
                  }}>
                    <thead>
                      <tr style={{
                        background: '#f8fafc',
                        borderBottom: '2px solid #e2e8f0'
                      }}>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#1e293b',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}>#</th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#1e293b',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}>Created By</th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#1e293b',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}>Certificate #</th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#1e293b',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}>Policy #</th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#1e293b',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}>Type</th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#1e293b',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}>Vehicle #</th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#1e293b',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}>Insured Person</th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#1e293b',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}>Start Date</th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#1e293b',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}>End Date</th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#1e293b',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}>Premium</th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#1e293b',
                          cursor: 'pointer',
                          userSelect: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}>
                          Issued On <span style={{ fontSize: '0.8rem' }}>↓</span>
                        </th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#1e293b'
                        }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {declarations.map((declaration, index) => (
                        <tr key={declaration.id} style={{
                          borderBottom: '1px solid #e2e8f0',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                          <td style={{ padding: '1rem', color: '#64748b' }}>
                            {declaration.row_number || index + 1 + (declarationsPagination.page - 1) * declarationsPagination.limit}
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <div style={{ fontWeight: '500', color: '#1e293b' }}>
                              {declaration.created_by_name || 'Agent'}
                            </div>
                            {declaration.employee_id && (
                              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                                Employee
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '1rem', color: '#1e293b', fontFamily: 'monospace' }}>
                            {declaration.certificate_number || 'N/A'}
                          </td>
                          <td style={{ padding: '1rem', color: '#1e293b', fontFamily: 'monospace' }}>
                            {declaration.policy_number || 'N/A'}
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '12px',
                              fontSize: '0.8rem',
                              fontWeight: '500',
                              background: declaration.certificate_type?.toLowerCase().includes('type a') ? '#dbeafe' :
                                          declaration.certificate_type?.toLowerCase().includes('type b') ? '#fef3c7' :
                                          declaration.certificate_type?.toLowerCase().includes('type c') ? '#d1fae5' : '#fce7f3',
                              color: declaration.certificate_type?.toLowerCase().includes('type a') ? '#1e40af' :
                                     declaration.certificate_type?.toLowerCase().includes('type b') ? '#92400e' :
                                     declaration.certificate_type?.toLowerCase().includes('type c') ? '#065f46' : '#831843'
                            }}>
                              {declaration.certificate_type || 'N/A'}
                            </span>
                          </td>
                          <td style={{ padding: '1rem', color: '#1e293b', fontFamily: 'monospace' }}>
                            {declaration.vehicle_number || 'N/A'}
                          </td>
                          <td style={{ padding: '1rem', color: '#1e293b' }}>
                            {declaration.insured_person || 'N/A'}
                          </td>
                          <td style={{ padding: '1rem', color: '#64748b' }}>
                            {formatDate(declaration.start_date) || 'N/A'}
                          </td>
                          <td style={{ padding: '1rem', color: '#64748b' }}>
                            {formatDate(declaration.end_date) || 'N/A'}
                          </td>
                          <td style={{ padding: '1rem', color: '#1e293b', fontWeight: '600' }}>
                            {formatCurrency(declaration.premium) || 'Ksh 0'}
                          </td>
                          <td style={{ padding: '1rem', color: '#64748b' }}>
                            {formatDate(declaration.issuance_on) || 'N/A'}
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => {
                                  alert(`Certificate: ${declaration.certificate_number}\nPolicy: ${declaration.policy_number}`);
                                }}
                                style={{
                                  background: '#225741',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.5rem 1rem',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  fontWeight: '500'
                                }}
                              >
                                View
                              </button>
                              {declaration.certificate_number && (
                                <button
                                  type="button"
                                  onClick={() => downloadCertificate(declaration.certificate_number)}
                                  disabled={certDownloadingNumber === declaration.certificate_number}
                                  style={{
                                    background: '#1a4736',
                                    color: 'white',
                                    border: 'none',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '6px',
                                    cursor: certDownloadingNumber === declaration.certificate_number ? 'not-allowed' : 'pointer',
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    opacity: certDownloadingNumber === declaration.certificate_number ? 0.8 : 1
                                  }}
                                  title="Download certificate PDF"
                                >
                                  {certDownloadingNumber === declaration.certificate_number ? '…' : 'Download'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {declarationsPagination.totalPages > 1 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1.5rem 2rem',
                    borderTop: '1px solid #e2e8f0'
                  }}>
                    <button
                      onClick={() => fetchDeclarations(declarationsPagination.page - 1)}
                      disabled={declarationsPagination.page === 1}
                      style={{
                        padding: '0.5rem 1rem',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        background: declarationsPagination.page === 1 ? '#f1f5f9' : 'white',
                        color: declarationsPagination.page === 1 ? '#94a3b8' : '#1e293b',
                        cursor: declarationsPagination.page === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      ← Previous
                    </button>
                    <span style={{ color: '#64748b', fontSize: '0.9rem' }}>
                      Page {declarationsPagination.page} of {declarationsPagination.totalPages} ({declarationsPagination.total} total)
                    </span>
                    <button
                      onClick={() => fetchDeclarations(declarationsPagination.page + 1)}
                      disabled={declarationsPagination.page >= declarationsPagination.totalPages}
                      style={{
                        padding: '0.5rem 1rem',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        background: declarationsPagination.page >= declarationsPagination.totalPages ? '#f1f5f9' : 'white',
                        color: declarationsPagination.page >= declarationsPagination.totalPages ? '#94a3b8' : '#1e293b',
                        cursor: declarationsPagination.page >= declarationsPagination.totalPages ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Employee Covers Modal */}
        {selectedEmployee && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '2rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '90%',
              maxHeight: '90%',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0, color: '#2c3e50' }}>
                  📋 Covers by {selectedEmployee.full_name || selectedEmployee.username}
                </h2>
                <button
                  onClick={handleCloseEmployeeCovers}
                  style={{
                    background: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  ✕ Close
                </button>
              </div>

              {employeeCoversLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div className="spinner-large"></div>
                  <p>Loading covers...</p>
                </div>
              ) : employeeCovers.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📭</div>
                  <h3>No covers found</h3>
                  <p>This employee hasn't issued any covers yet.</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="business-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Client name</th>
                        <th>Policy Number</th>
                        <th>Cover Type</th>
                        <th>Vehicle Class</th>
                        <th>Premium</th>
                        <th>Payment Status</th>
                        <th>Certificate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeeCovers.map((cover) => (
                        <tr key={cover.id || cover.client_id}>
                          <td>{formatDate(cover.issuance_on || cover.transaction_date || cover.created_at)}</td>
                          <td>{cover.insured_person || cover.client_id || 'N/A'}</td>
                          <td>{cover.policy_number || 'N/A'}</td>
                          <td>{cover.certificate_type || cover.cover_type || 'N/A'}</td>
                          <td>{cover.vehicle_class || 'N/A'}</td>
                          <td>{formatCurrency(cover.premium || cover.premium_amount || 0)}</td>
                          <td>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '12px',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              background: cover.payment_status === 'completed' ? '#d4edda' : '#fff3cd',
                              color: cover.payment_status === 'completed' ? '#155724' : '#856404'
                            }}>
                              {cover.payment_status || 'pending'}
                            </span>
                          </td>
                          <td>
                            {cover.certificate_number ? (
                              <span className="cert-cell-with-download">
                                <span style={{ color: '#27ae60', fontWeight: 'bold' }}>✅ {cover.certificate_number}</span>
                                <button
                                  type="button"
                                  className="btn-download-cert"
                                  disabled={certDownloadingNumber === cover.certificate_number}
                                  onClick={() => downloadCertificate(cover.certificate_number)}
                                  title="Download certificate PDF"
                                >
                                  {certDownloadingNumber === cover.certificate_number ? '…' : 'Download'}
                                </button>
                              </span>
                            ) : (
                              <span style={{ color: '#95a5a6' }}>Pending</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Employees Section */}
        {showEmployees && !showCoverPurchase && (
          <div className="employees-section" style={{ 
            background: 'white', 
            padding: '2rem', 
            borderRadius: '12px', 
            marginBottom: '2rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setShowEmployees(false)}
                  style={{
                    background: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}
                >
                  ← Back to Dashboard
                </button>
                <h2 style={{ margin: 0, color: '#2c3e50' }}>👥 Employee Management</h2>
              </div>
              <button
                onClick={() => {
                  setShowEmployeeForm(true);
                  setEditingEmployee(null);
                  setEmployeeForm({ username: '', password: '', email: '', phone: '', full_name: '' });
                  setEmployeesError('');
                }}
                style={{
                  background: '#27ae60',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9rem'
                }}
              >
                ➕ Add Employee
              </button>
            </div>

            {employeesError && (
              <div style={{ 
                background: '#fee', 
                color: '#c33', 
                padding: '1rem', 
                borderRadius: '8px', 
                marginBottom: '1rem',
                border: '1px solid #fcc'
              }}>
                ⚠️ {employeesError}
              </div>
            )}

            {showEmployeeForm && (
              <div style={{ 
                background: '#f8f9fa', 
                padding: '1.5rem', 
                borderRadius: '8px', 
                marginBottom: '1.5rem',
                border: '1px solid #dee2e6'
              }}>
                <h3 style={{ marginTop: 0, color: '#2c3e50' }}>
                  {editingEmployee ? '✏️ Edit Employee' : '➕ Create New Employee'}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                  <div>
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                      Username
                    </label>
                    <input
                      type="text"
                      value={employeeForm.username}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, username: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem'
                      }}
                      placeholder={editingEmployee ? "Enter username" : "e.g., Denis (will become Denis@agency_code)"}
                    />
                    {!editingEmployee && (
                      <small style={{ color: '#7f8c8d', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
                        💡 Username will be auto-generated as: <strong>name@agency_code</strong> (e.g., Denis@one for "One Insurance Agency")
                      </small>
                    )}
                  </div>
                  <div>
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                      Password {editingEmployee ? '(leave blank to keep current)' : '*'}
                    </label>
                    <input
                      type="password"
                      value={employeeForm.password}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, password: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem'
                      }}
                      placeholder={editingEmployee ? "Enter new password (optional)" : "Enter password (min 6 chars)"}
                      required={!editingEmployee}
                    />
                  </div>
                  <div>
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={employeeForm.full_name}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, full_name: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem'
                      }}
                      placeholder="Enter full name"
                    />
                  </div>
                  <div>
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      value={employeeForm.email}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem'
                      }}
                      placeholder="Enter email address"
                    />
                  </div>
                  <div>
                    <label style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={employeeForm.phone}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem'
                      }}
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <button
                    onClick={editingEmployee ? () => handleUpdateEmployee(editingEmployee) : handleCreateEmployee}
                    disabled={employeesLoading || (!editingEmployee && !employeeForm.password) || (!editingEmployee && !employeeForm.full_name && !employeeForm.username)}
                    style={{
                      background: (employeesLoading || (!editingEmployee && !employeeForm.password) || (!editingEmployee && !employeeForm.full_name && !employeeForm.username)) ? '#95a5a6' : '#27ae60',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 2rem',
                      borderRadius: '8px',
                      cursor: (employeesLoading || !employeeForm.username || (!editingEmployee && !employeeForm.password)) ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '1rem'
                    }}
                  >
                    {employeesLoading ? 'Saving...' : (editingEmployee ? '💾 Update Employee' : '💾 Create Employee')}
                  </button>
                  <button
                    onClick={handleCancelEmployeeForm}
                    disabled={employeesLoading}
                    style={{
                      background: '#95a5a6',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 2rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '1rem'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {employeesLoading && !showEmployeeForm ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div className="spinner-large"></div>
                <p>Loading employees...</p>
              </div>
            ) : employees.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">👥</div>
                <h3>No employees found</h3>
                <p>Create your first employee to allow them to issue covers on your behalf.</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="business-table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Full Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Last Login</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((employee) => (
                      <tr key={employee.id}>
                        <td>{employee.username}</td>
                        <td>{employee.full_name || 'N/A'}</td>
                        <td>{employee.email || 'N/A'}</td>
                        <td>{employee.phone || 'N/A'}</td>
                        <td>
                          {employee.is_active ? (
                            <span style={{ color: '#27ae60', fontWeight: 'bold' }}>✅ Active</span>
                          ) : (
                            <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>❌ Inactive</span>
                          )}
                        </td>
                        <td>{employee.last_login ? formatDate(employee.last_login) : 'Never'}</td>
                        <td>{formatDate(employee.created_at)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleViewEmployeeCovers(employee)}
                              style={{
                                background: '#27ae60',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.85rem'
                              }}
                            >
                              📋 View Covers
                            </button>
                            <button
                              onClick={() => handleEditEmployee(employee)}
                              style={{
                                background: '#3498db',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.85rem'
                              }}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              type="button"
                              title={
                                employee.email
                                  ? 'Email a new password to this employee'
                                  : 'Add an email on the employee record first'
                              }
                              onClick={() => handleResendLoginEmail(employee)}
                              disabled={employeesLoading || !employee.email}
                              style={{
                                background: '#8e44ad',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                cursor: employeesLoading || !employee.email ? 'not-allowed' : 'pointer',
                                fontSize: '0.85rem',
                                opacity: employeesLoading || !employee.email ? 0.55 : 1
                              }}
                            >
                              ✉️ Resend login email
                            </button>
                            <button
                              onClick={() => handleToggleEmployeeStatus(employee.id, employee.is_active)}
                              disabled={employeesLoading}
                              style={{
                                background: employee.is_active ? '#f39c12' : '#27ae60',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                cursor: employeesLoading ? 'not-allowed' : 'pointer',
                                fontSize: '0.85rem',
                                opacity: employeesLoading ? 0.6 : 1
                              }}
                            >
                              {employee.is_active ? '🚫 Deactivate' : '✅ Activate'}
                            </button>
                            <button
                              onClick={() => handleDeleteEmployee(employee.id)}
                              style={{
                                background: '#e74c3c',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.85rem'
                              }}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {showCertificateRecovery && !showCoverPurchase && (
          <div className="business-section" style={{ padding: '0 0.75rem' }}>
            <h2>Check Certificate Status</h2>
            <p style={{ color: '#555', marginBottom: '1rem', maxWidth: '48rem', lineHeight: 1.5 }}>
              Covers assigned to you where a certificate has not been issued yet.
              Search by vehicle registration, policy number, client ID, insured name, payment reference, or M-Pesa code, then recover or fix contact details and retry.
              Use <strong>With date</strong> when another cover on the same plate is still active—the suggested start is the day after that cover ends (aligned with DMVIC when available).
              When the error is only that details do not match NTSA records, use <strong>Correct plate</strong> to enter the full registration from NTSA, then issuance is queued automatically.
            </p>
            {certRetryMessage && (
              <div
                className={
                  certRetryMessage.startsWith('Certificate queued') || certRetryMessage.includes('successfully')
                    ? 'success-alert'
                    : 'error-alert'
                }
                style={{ marginBottom: '12px' }}
              >
                {certRetryMessage}
              </div>
            )}
            {certRecoveryError && (
              <div className="error-alert" style={{ marginBottom: '12px' }}>
                ⚠️ {certRecoveryError}
              </div>
            )}
            <div className="filters-section" style={{ marginBottom: '1rem' }}>
              <div className="filter-group">
                <input
                  type="text"
                  placeholder="Search by vehicle, policy, client ID, insured name, reference, or M-Pesa code…"
                  value={certRecoverySearch}
                  onChange={(e) => setCertRecoverySearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && fetchCertificateRecovery(1)}
                  className="filter-input"
                />
                <select
                  className="filter-select"
                  value={certRecoveryStatusScope}
                  onChange={(e) => setCertRecoveryStatusScope(e.target.value)}
                  style={{ marginLeft: '8px' }}
                >
                  <option value="pending">Pending only</option>
                  <option value="all">All statuses</option>
                </select>
                <p style={{ margin: '8px 0 0 0', color: '#555', fontSize: '0.9rem' }}>
                  Tip: choose a status in the dropdown first, then click Search.
                </p>
                <button className="btn-filter" type="button" onClick={() => fetchCertificateRecovery(1)} style={{ marginTop: '8px' }}>
                  Search
                </button>
                <button
                  className="btn-retry-cert"
                  type="button"
                  style={{ marginTop: '8px', marginLeft: '8px' }}
                  disabled={certBatchRetrying || certRecoveryItems.length === 0}
                  onClick={retryPendingCertificatesBatch}
                >
                  {certBatchRetrying ? 'Queuing Pending…' : 'Recover All Pending'}
                </button>
              </div>
            </div>
            {certRecoveryLoading ? (
              <div className="loading-state">
                <div className="spinner-large"></div>
                <p>Loading…</p>
              </div>
            ) : certRecoveryItems.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">✅</div>
                <h3>
                  {certRecoveryStatusScope === 'all'
                    ? 'No covers found'
                    : certRecoveryStatusScope === 'connection_closed'
                      ? 'No connection-closed certificate checks'
                      : 'No pending certificate checks'}
                </h3>
                <p>
                  {certRecoveryStatusScope === 'all'
                    ? 'No paid covers match your current search.'
                    : certRecoveryStatusScope === 'connection_closed'
                      ? 'No covers with "Connection is closed" were found in your scope.'
                    : 'All your paid covers have certificates, or nothing matches your search.'}
                </p>
              </div>
            ) : (
              <>
                <div className="table-container" style={{ overflowX: 'auto' }}>
                  <table className="business-table">
                    <thead>
                      <tr>
                        <th>Payment completed</th>
                        <th>Insured</th>
                        <th>Vehicle registration</th>
                        <th>Policy</th>
                        <th>Cover</th>
                        <th>Payment ref</th>
                        <th>Status / note</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {certRecoveryItems.map((row) => {
                        const isIssued = row.certificate_status === 'issued' || !!String(row.dmvic_certificate_number || '').trim();
                        return (
                        <tr key={row.client_id}>
                          <td>{formatDate(row.payment_completed_at)}</td>
                          <td>{row.insured_name || row.client_id || 'N/A'}</td>
                          <td>{row.registration_number || '—'}</td>
                          <td>{row.policy_number || 'N/A'}</td>
                          <td>
                            {[row.cover_type, row.vehicle_class].filter(Boolean).join(' · ') || 'N/A'}
                          </td>
                          <td style={{ fontSize: '0.85rem' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                              <span style={{ wordBreak: 'break-all' }}>{row.payment_reference || '—'}</span>
                              {row.payment_confirmation_code && (
                                <span style={{ color: '#0f5132', fontWeight: 700 }}>
                                  M-Pesa: {row.payment_confirmation_code}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span title={row.certificate_last_error || ''} style={{ fontSize: '0.9rem' }}>
                              {isIssued ? 'Issued' : (row.certificate_status === 'failed' ? 'Failed' : 'Not issued')}
                              {row.certificate_last_error
                                ? ` — ${String(row.certificate_last_error).slice(0, 100)}${String(row.certificate_last_error).length > 100 ? '…' : ''}`
                                : ''}
                            </span>
                          </td>
                            <td>
                            <button
                              type="button"
                              className="btn-retry-cert"
                              disabled={certBatchRetrying || certRetryingClientId === row.client_id || isIssued}
                              onClick={() => retryCertificateForClient(row.client_id)}
                            >
                              {certRetryingClientId === row.client_id ? 'Queuing…' : (isIssued ? 'Issued' : 'Recover')}
                            </button>
                            {!isIssued && (
                              <button
                                type="button"
                                className="btn-retry-date"
                                style={{ marginLeft: '8px' }}
                                disabled={certBatchRetrying || certRetryingClientId === row.client_id}
                                onClick={() => openCertRetryDateModal(row)}
                                title="Retry with a new start date (e.g. day after another cover on this plate ends)"
                              >
                                With date
                              </button>
                            )}
                              {row.certificate_status === 'failed' && !isIssued && (
                                <button
                                  type="button"
                                  className="btn-filter"
                                  style={{ marginLeft: '8px' }}
                                  disabled={certBatchRetrying || certRetryingClientId === row.client_id}
                                  onClick={() => openFixAndRetry(row)}
                                >
                                  Fix & Retry
                                </button>
                              )}
                              {row.ntsa_registration_mismatch_only && !isIssued && (
                                <button
                                  type="button"
                                  className="btn-correct-plate"
                                  style={{ marginLeft: '8px' }}
                                  disabled={certBatchRetrying || certRetryingClientId === row.client_id}
                                  onClick={() => correctPlateNtsaMismatchAndRecover(row)}
                                  title="Wrong or incomplete plate: enter full NTSA registration, sync vehicle, then queue certificate"
                                >
                                  Correct plate
                                </button>
                              )}
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </div>
                {certRecoveryPagination.totalPages > 1 && (
                  <div className="pagination">
                    <button
                      type="button"
                      className="btn-pagination"
                      onClick={() => fetchCertificateRecovery(certRecoveryPagination.page - 1)}
                      disabled={certRecoveryPagination.page === 1}
                    >
                      ← Previous
                    </button>
                    <span className="pagination-info">
                      Page {certRecoveryPagination.page} of {certRecoveryPagination.totalPages} ({certRecoveryPagination.total}{' '}
                      total)
                    </span>
                    <button
                      type="button"
                      className="btn-pagination"
                      onClick={() => fetchCertificateRecovery(certRecoveryPagination.page + 1)}
                      disabled={certRecoveryPagination.page >= certRecoveryPagination.totalPages}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {showCompContinuations && !showCoverPurchase && (
          <CompContinuations
            authToken={localStorage.getItem('agentToken')}
            agentProfile={profile || user}
            apiBase="/api/agent/comp-continuations"
          />
        )}

        {/* Statistics Cards */}
        {!showProfile && !showDeclarations && !showEmployees && !showCoverPurchase && !showCertificateRecovery && !showCompContinuations && (
          <>
            {profile?.selfRegistered && (
              <SelfRegistrationOnboarding
                profile={profile}
                onGoToKyc={() => handleViewChange('profile')}
              />
            )}
            <AgentBusinessAnalytics statistics={statistics} charts={charts} />

            {/* Filters */}
            <div className="filters-section">
              <div className="filter-group">
                <input
                  type="text"
                  placeholder="Search by Client ID, Policy, Vehicle, Reference, or M-Pesa code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="filter-input"
                />
                <button className="btn-filter" onClick={handleSearch}>
                  Search
                </button>
              </div>
              <div className="filter-group">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="filter-input"
                  placeholder="Start Date"
                />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="filter-input"
                  placeholder="End Date"
                />
                <button className="btn-filter" onClick={handleDateFilter}>
                  Filter
                </button>
              </div>
            </div>

            {/* Business Table */}
            <div className="business-section">
              <h2>Your Business Transactions</h2>
              {certRetryMessage && (
                <div className={certRetryMessage.startsWith('Certificate queued') ? 'success-alert' : 'error-alert'} style={{ marginBottom: '12px' }}>
                  {certRetryMessage}
                </div>
              )}
              {error && (
                <div className="error-alert">
                  ⚠️ {error}
                </div>
              )}
              {isLoading ? (
                <div className="loading-state">
                  <div className="spinner-large"></div>
                  <p>Loading business data...</p>
                </div>
              ) : business.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📭</div>
                  <h3>No transactions found</h3>
                  <p>Your business transactions will appear here once you start processing policies.</p>
                </div>
              ) : (
                <>
                  <div className="table-container" style={{ overflowX: 'auto' }}>
                    <table className="business-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Client name</th>
                          <th>Vehicle registration</th>
                          <th>Policy Number</th>
                          <th>Cover Type</th>
                          <th>Vehicle Class</th>
                          <th>Premium</th>
                          <th style={{ background: '#1A4736', color: 'white' }}>Gross Commission</th>
                          <th style={{ background: '#e74c3c', color: 'white' }}>Tax (10%)</th>
                          <th style={{ background: '#27ae60', color: 'white' }}>Net Commission</th>
                          <th>Payment Status</th>
                          <th>Certificate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {business.map((transaction) => {
                          const premium = parseFloat(transaction.premium_amount) || 0;
                          const isNet = transaction.is_net === true
                            || transaction.is_net === 'true'
                            || String(transaction.cover_type || '').toUpperCase() === 'TOR';
                          const grossCommission = isNet ? 0 : premium * 0.10;
                          const tax = isNet ? 0 : grossCommission * 0.10;
                          const netCommission = grossCommission - tax;
                          const registration = [
                            transaction.registration_number,
                            transaction.vehicle_registration,
                            transaction.vehicle_number
                          ].map((v) => (v == null ? '' : String(v).trim())).find(Boolean) || 'N/A';
                          
                          return (
                            <tr key={transaction.id}>
                              <td>{formatDate(transaction.transaction_date)}</td>
                              <td>{transaction.insured_name || transaction.client_id || 'N/A'}</td>
                              <td>{registration}</td>
                              <td>{transaction.policy_number || 'N/A'}</td>
                              <td>{transaction.cover_type || 'N/A'}</td>
                              <td>{transaction.vehicle_class || 'N/A'}</td>
                              <td className="amount-cell">{formatCurrency(premium)}</td>
                              <td className="amount-cell" style={{ background: '#f0f0ff', fontWeight: '600' }}>
                                {formatCurrency(grossCommission)}
                              </td>
                              <td className="amount-cell" style={{ background: '#fff0f0', color: '#e74c3c', fontWeight: '600' }}>
                                -{formatCurrency(tax)}
                              </td>
                              <td className="amount-cell" style={{ background: '#f0fff0', color: '#27ae60', fontWeight: '700' }}>
                                {formatCurrency(netCommission)}
                              </td>
                              <td>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                  {transaction.payment_confirmation_code && (
                                    <div style={{ fontSize: '0.78rem', color: '#0f5132', fontWeight: 700 }}>
                                      M-Pesa: {transaction.payment_confirmation_code}
                                    </div>
                                  )}
                                  {transaction.payment_reference && (
                                    <div style={{ fontSize: '0.72rem', color: '#64748b', wordBreak: 'break-all' }}>
                                      Ref: {transaction.payment_reference}
                                    </div>
                                  )}
                                  {getStatusBadge(transaction.payment_status)}
                                </div>
                              </td>
                              <td>
                                {transaction.certificate_number ? (
                                  <span className="cert-cell-with-download">
                                    <span>{transaction.certificate_number}</span>
                                    <button
                                      type="button"
                                      className="btn-download-cert"
                                      disabled={certDownloadingNumber === transaction.certificate_number}
                                      onClick={() => downloadCertificate(transaction.certificate_number)}
                                      title="Download certificate PDF"
                                    >
                                      {certDownloadingNumber === transaction.certificate_number ? '…' : 'Download'}
                                    </button>
                                  </span>
                                ) : (transaction.payment_status === 'completed' || transaction.payment_status === 'paid' || transaction.payment_status === 'success') ? (
                                  <span className="cert-failed-cell">
                                    <span title={transaction.certificate_last_error || 'Certificate not yet issued'}>
                                      {transaction.certificate_status === 'failed' ? (
                                        <>
                                          Failed
                                          {(transaction.certificate_last_error || '').includes('INSUFFICIENT_INVENTORY') || (transaction.certificate_last_error || '').includes('insufficient inventory')
                                            ? ' (inventory)'
                                            : ''}
                                        </>
                                      ) : (
                                        'Not issued'
                                      )}
                                    </span>
                                    <button
                                      type="button"
                                      className="btn-retry-cert"
                                      disabled={certRetryingClientId === transaction.client_id}
                                      onClick={() => retryCertificateForClient(transaction.client_id)}
                                      title="Queue certificate issuance so the cover is issued"
                                    >
                                      {certRetryingClientId === transaction.client_id ? 'Queuing…' : 'Retry cert'}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-retry-date"
                                      style={{ marginLeft: '6px' }}
                                      disabled={certRetryingClientId === transaction.client_id}
                                      onClick={() => openCertRetryDateModal({
                                        client_id: transaction.client_id,
                                        insured_name: transaction.insured_name,
                                        registration_number: registration === 'N/A' ? '' : registration
                                      })}
                                      title="Retry with a new start date if another cover on this plate overlaps"
                                    >
                                      With date
                                    </button>
                                  </span>
                                ) : (
                                  'Pending'
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="btn-pagination"
                        onClick={() => fetchBusiness(pagination.page - 1)}
                        disabled={pagination.page === 1}
                      >
                        ← Previous
                      </button>
                      <span className="pagination-info">
                        Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                      </span>
                      <button
                        className="btn-pagination"
                        onClick={() => fetchBusiness(pagination.page + 1)}
                        disabled={pagination.page >= pagination.totalPages}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </main>

      {certRetryDateModal && (
        <div className="paid-no-cert-modal-overlay" onClick={closeCertRetryDateModal} role="dialog" aria-modal="true" aria-labelledby="agent-cert-retry-date-title">
          <div className="paid-no-cert-modal" onClick={(e) => e.stopPropagation()}>
            <div className="paid-no-cert-modal-header">
              <h3 id="agent-cert-retry-date-title">Re-issue after active cover ends</h3>
              <button type="button" className="paid-no-cert-modal-close" onClick={closeCertRetryDateModal} aria-label="Close">×</button>
            </div>
            <p className="paid-no-cert-modal-meta">
              {certRetryDateModal.insured_name || certRetryDateModal.client_id} · {certRetryDateModal.registration_number || '—'}
            </p>
            {certRetryModalLoadingSuggested ? (
              <p className="paid-no-cert-modal-loading">Checking DMVIC for the next available start date…</p>
            ) : certRetryModalSuggested ? (
              <div className="paid-no-cert-modal-suggested">
                {certRetryModalOtherEnd && (
                  <p style={{ marginTop: 0 }}>
                    Existing active cover ends on <strong>{formatPolicyDateOnly(certRetryModalOtherEnd)}</strong>.
                  </p>
                )}
                <p>
                  Next available start date: <strong>{formatPolicyDateOnly(certRetryModalSuggested)}</strong>
                </p>
                <p className="paid-no-cert-modal-suggested-note" style={{ marginTop: '0.35rem' }}>
                  Day after the previous policy expires. This date will be saved and sent to DMVIC.
                </p>
              </div>
            ) : (
              <div className="paid-no-cert-modal-suggested" style={{ color: '#721c24' }}>
                <p style={{ marginTop: 0, fontWeight: 600 }}>Cannot auto-reissue yet.</p>
                <p className="paid-no-cert-modal-suggested-note">
                  The next available start date is more than 30 days away, or DMVIC did not return one. Try again when the current policy is within 30 days of expiry, or contact D-MVIC support.
                </p>
              </div>
            )}
            <div className="paid-no-cert-modal-actions">
              <button
                type="button"
                className="btn-retry-date-submit"
                onClick={submitCertRetryWithDate}
                disabled={!certRetryModalSuggested || certRetryModalLoadingSuggested || certRetryingClientId === certRetryDateModal.client_id}
              >
                {certRetryModalSuggested
                  ? `Re-issue certificate from ${formatPolicyDateOnly(certRetryModalSuggested)}`
                  : 'Re-issue certificate'}
              </button>
              <button type="button" className="btn-retry-date-cancel" onClick={closeCertRetryDateModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AgentDashboard;
